<template>
  <main class="home-dashboard">
    <DashboardSkeleton v-if="isMenuLoading" :show-recent="false" />
    <DashboardQuickMenu v-else
                        :can-customize="hasWidgetData"
                        :items="quickMenuItems"
                        @navigate="navigateTo"
    />

    <template v-if="canReadContent">
      <DashboardSkeleton v-if="isContentLoading" :show-quick="false" />
      <DashboardRecentContents v-else
                               :can-create-assignment="contentPermissions.canCreateAssignment"
                               :can-edit="contentPermissions.canEdit"
                               :items="recentContentItems"
      />
    </template>

    <DashboardCustomizeModal :max-menu-items="maxMenuItems"
                             :min-menu-items="minMenuItems"
                             :widgets="widgets"
                             @reset="resetMenuToDefault"
                             @save="updateMenuSubmit"
    />
  </main>
</template>

<script>
import cloneDeep from "lodash/cloneDeep";
import store from "@/core/services/index";
import DashboardCustomizeModal from "./components/DashboardCustomizeModal.vue";
import DashboardQuickMenu from "./components/DashboardQuickMenu.vue";
import DashboardRecentContents from "./components/DashboardRecentContents.vue";
import DashboardSkeleton from "./components/DashboardSkeleton.vue";

import menu_module, {
  BASE_URL as MENU_BASE_URL,
  ERROR as MENU_ERROR,
  GET_ITEM as MENU_GET_ITEM,
  ITEM as MENU_ITEM,
  MODULE_NAME as MENU_MODULE_NAME,
  RESET_BASE_URL,
  RESET_MENU,
  UPDATE_ITEM as MENU_UPDATE_ITEM,
} from "@/core/services/store/menu.module";

import content_module, {
  GET_ITEMS as CONTENT_GET_ITEMS,
  ITEMS as CONTENT_ITEMS,
  MODULE_NAME as CONTENT_MODULE_NAME,
} from "@/core/services/store/content.module";

const PERMISSION_BY_MENU_KEY = {
  "menu.company_dashboard.add_users": "create_user",
  "menu.company_dashboard.catalog_trainings": "read_training",
  "menu.company_dashboard.create_assignment": "create_assignment",
  "menu.company_dashboard.create_contact_material": "create_content",
  "menu.company_dashboard.education_reports": "read_report",
  "menu.company_dashboard.gaming_settings": "read_point",
  "menu.company_dashboard.manage_posts": "read_sharesmanagement",
  "menu.company_dashboard.user_requests": "read_user",
};

export default {
  name: "HomeDashboard",
  components: {
    DashboardCustomizeModal,
    DashboardQuickMenu,
    DashboardRecentContents,
    DashboardSkeleton,
  },
  data() {
    return {
      maxMenuItems: 8,
      minMenuItems: 4,
      activeWidgets: { data: { active: [] } },
      apiContentUrl: "api/dashboard-content",
      isContentLoading: true,
      isMenuLoading: true,
    };
  },
  beforeCreate() {
    const registerStoreModule = (moduleName, storeModule) => {
      if (!(store && store.state && store.state[moduleName])) {
        store.registerModule(moduleName, storeModule);
      }
    };

    registerStoreModule(MENU_MODULE_NAME, menu_module);
    registerStoreModule(CONTENT_MODULE_NAME, content_module);
  },
  computed: {
    canReadContent() {
      return this.permissionNames.has("read_content");
    },
    contentItems() {
      return store.getters[CONTENT_MODULE_NAME + "/" + CONTENT_ITEMS] || [];
    },
    contentPermissions() {
      return {
        canCreateAssignment: this.permissionNames.has("create_assignment"),
        canEdit: this.permissionNames.has("create_content"),
      };
    },
    dateFormatter() {
      const locale =
          this.$i18n && this.$i18n.locale ? this.$i18n.locale : "tr-TR";

      return new Intl.DateTimeFormat(locale, {
        day: "2-digit",
        hour: "2-digit",
        hourCycle: "h23",
        minute: "2-digit",
        month: "long",
        year: "numeric",
      });
    },
    errors() {
      return store.getters[MENU_MODULE_NAME + "/" + MENU_ERROR];
    },
    hasWidgetData() {
      return Boolean(this.widgets && this.widgets.data);
    },
    permissionNames() {
      // SecureLS okuması pahalı (AES çözme + decompress), tek seferde okunup
      // Set'e alınıyor; aksi halde her izin kontrolü storage'a gidiyor.
      const permissions = this.secureLocalStorage("permissions") || [];

      return new Set(permissions.map((permission) => permission.name));
    },
    quickMenuItems() {
      const items = this.activeWidgets.data.active || [];

      return items.map((item) => ({
        ...item,
        allowed: this.getPermission(item.key_name),
      }));
    },
    recentContentItems() {
      return this.contentItems.slice(0, 4).map((item) => ({
        ...item,
        createdAtLabel: this.formatDate(item.created_at),
      }));
    },
    widgets() {
      return store.getters[MENU_MODULE_NAME + "/" + MENU_ITEM] || {};
    },
  },
  methods: {
    formatDate(date) {
      const parsedDate = new Date(date);

      return Number.isNaN(parsedDate.getTime())
          ? ""
          : this.dateFormatter.format(parsedDate);
    },
    getPermission(keyName) {
      const required = PERMISSION_BY_MENU_KEY[keyName];

      return !required || this.permissionNames.has(required);
    },
    getContentItems() {
      return this.$store.dispatch(
          CONTENT_MODULE_NAME + "/" + CONTENT_GET_ITEMS,
          {
            url: this.apiContentUrl,
          }
      );
    },
    getMenuItems() {
      return this.$store
          .dispatch(MENU_MODULE_NAME + "/" + MENU_GET_ITEM, {
            url: MENU_BASE_URL,
          })
          .then(() => {
            this.activeWidgets = this.hasWidgetData
                ? cloneDeep(this.widgets)
                : { data: { active: [] } };
          });
    },
    navigateTo(routeName) {
      if (routeName) {
        this.$router.push({ name: routeName });
      }
    },
    resetMenuToDefault() {
      return this.$store.dispatch(MENU_MODULE_NAME + "/" + RESET_MENU, {
        url: RESET_BASE_URL,
        contents: this.widgets,
      });
    },
    updateMenuSubmit() {
      return this.$store
          .dispatch(MENU_MODULE_NAME + "/" + MENU_UPDATE_ITEM, {
            url: MENU_BASE_URL,
            contents: this.widgets,
          })
          .then(() => {
            if (!this.errors) {
              this.activeWidgets = cloneDeep(this.widgets);
            }
          });
    },
  },
  mounted() {
    // İki istek birbirini beklemiyor; hızlı biten bölüm hemen ekrana geliyor.
    this.getMenuItems().finally(() => {
      this.isMenuLoading = false;
    });

    if (this.canReadContent) {
      this.getContentItems().finally(() => {
        this.isContentLoading = false;
      });
    } else {
      this.isContentLoading = false;
    }
  },
};
</script>

<style scoped>
.home-dashboard {
  width: 100%;
}

@media (max-width: 767px) {
  .home-dashboard {
    box-sizing: border-box;
    padding: 0;
  }
}
</style>
